import { getToken, onMessage } from 'firebase/messaging';
import { initializeMessaging, vapidKey } from '../firebase/firebaseConfig';
import { useAxiosInstance } from '../api/axiosInstance';
import React from 'react'; // Added missing import for React

interface FCMTokenResponse {
  success: boolean;
  data?: any;
  error?: string;
  shouldReLogin?: boolean;
}

class FCMTokenService {
  private messaging: any = null;
  private currentToken: string | null = null;
  private refreshTimer: NodeJS.Timeout | null = null;
  private isInitialized: boolean = false;
  private axiosInstance: any = null;

  /**
   * Initialize FCM messaging and set up message listener
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      console.log('FCM Service: Initializing Firebase messaging...');
      
      this.messaging = await initializeMessaging();
      
      if (!this.messaging) {
        console.warn('FCM Service: Messaging not supported, skipping initialization');
        return;
      }

      // Set up message listener for foreground messages
      this.setupMessageListener();
      this.isInitialized = true;
      console.log('FCM Service: Successfully initialized');
    } catch (error) {
      console.error('FCM Service: Failed to initialize messaging:', error);
    }
  }

  /**
   * Set the axios instance for API calls
   */
  setAxiosInstance(axiosInstance: any): void {
    this.axiosInstance = axiosInstance;
  }

  /**
   * Request notification permission and get FCM token
   */
  async requestPermissionAndGetToken(): Promise<string | null> {
    try {
      console.log('FCM Service: Requesting notification permission...');
      
      // Request permission for notifications
      const permission = await Notification.requestPermission();
      console.log('FCM Service: Notification permission status:', permission);

      if (permission !== 'granted') {
        console.warn('FCM Service: Notification permission not granted');
        return null;
      }

      if (!this.messaging) {
        console.warn('FCM Service: Messaging not initialized');
        return null;
      }

      // Get FCM registration token
      console.log('FCM Service: Getting FCM registration token...');
      const token = await getToken(this.messaging, {
        vapidKey: vapidKey,
      });

      if (token) {
        console.log('FCM Service: Successfully obtained FCM token:', token.substring(0, 20) + '...');
        this.currentToken = token;
        return token;
      } else {
        console.warn('FCM Service: No registration token available');
        return null;
      }
    } catch (error) {
      console.error('FCM Service: Error getting FCM token:', error);
      return null;
    }
  }

  /**
   * Send FCM token to backend on login
   */
  async sendTokenOnLogin(token: string): Promise<FCMTokenResponse> {
    try {
      console.log('FCM Service: Sending FCM token to backend on login...');
      
      if (!this.axiosInstance) {
        console.warn('FCM Service: Axios instance not set yet, retrying in 1 second...');
        // Wait a bit and retry
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        if (!this.axiosInstance) {
          throw new Error('Axios instance not set. Call setAxiosInstance first.');
        }
      }

      const response = await this.axiosInstance.post('/notifications/fcm/login', {
        deviceTokens: [token], 
        integrationIdentifier: 'firebase-cloud-messaging',
        providerId: 'fcm', 
      });

      console.log('FCM Service: Login token response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('FCM Service: Error sending login token:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
        shouldReLogin: error.response?.data?.shouldReLogin || false,
      };
    }
  }

  /**
   * Refresh FCM token with backend
   */
  async refreshToken(): Promise<FCMTokenResponse> {
    try {
      console.log('FCM Service: Refreshing FCM token with backend...');
      
      // Get fresh token from Firebase
      const token = await this.requestPermissionAndGetToken();
      if (!token) {
        throw new Error('Failed to get fresh FCM token');
      }

      if (!this.axiosInstance) {
        console.warn('FCM Service: Axios instance not set yet, retrying in 1 second...');
        // Wait a bit and retry
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        if (!this.axiosInstance) {
          throw new Error('Axios instance not set. Call setAxiosInstance first.');
        }
      }

      const response = await this.axiosInstance.post('/notifications/fcm/refresh', {
        deviceTokens: [token], // Backend expects deviceTokens array
        providerId: 'fcm', // ChatOrPushProviderEnum.Fcm
      });

      console.log('FCM Service: Refresh token response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('FCM Service: Error refreshing token:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
        shouldReLogin: error.response?.data?.shouldReLogin || false,
      };
    }
  }

  /**
   * Set up periodic token refresh (every 60 minutes)
   */
  startPeriodicRefresh(): void {
    if (this.refreshTimer) {
      console.log('FCM Service: Periodic refresh already running');
      return;
    }

    const REFRESH_INTERVAL = 60 * 60 * 1000; // 60 minutes in milliseconds
    
    console.log('FCM Service: Starting periodic token refresh (every 60 minutes)');
    this.refreshTimer = setInterval(async () => {
      console.log('FCM Service: Executing periodic token refresh...');
      const result = await this.refreshToken();
      
      if (!result.success) {
        console.error('FCM Service: Periodic refresh failed:', result.error);
        if (result.shouldReLogin) {
          console.warn('FCM Service: Backend indicates re-login required');
          // You might want to emit an event or call a callback here
          // to handle re-login in the application
        }
      }
    }, REFRESH_INTERVAL);
  }

  /**
   * Stop periodic token refresh
   */
  stopPeriodicRefresh(): void {
    if (this.refreshTimer) {
      console.log('FCM Service: Stopping periodic token refresh');
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Set up message listener for foreground notifications
   */
  private setupMessageListener(): void {
    if (!this.messaging) return;

    console.log('FCM Service: Setting up foreground message listener');
    onMessage(this.messaging, (payload) => {
      console.log('FCM Service: Message received in foreground:', payload);
      
      // Handle the message (you can customize this based on your needs)
      if (payload.notification) {
        const { title, body } = payload.notification;
        
        // Show browser notification if permission is granted
        if (Notification.permission === 'granted') {
          new Notification(title || 'Notification', {
            body: body || 'You have a new notification',
            icon: '/android/android-launchericon-192-192.png', // Use larger icon for better display
          });
        }
      }
    });
  }

  /**
   * Get current token
   */
  getCurrentToken(): string | null {
    return this.currentToken;
  }

  /**
   * Setup FCM on login
   */
  async setupFCMOnLogin(): Promise<void> {
    try {
      console.log('FCM Service: Setting up FCM on user login...');
      
      // Initialize messaging if not already done
      await this.initialize();
      
      // Request permission and get token
      const token = await this.requestPermissionAndGetToken();
      
      if (token) {
        // Send token to backend
        const result = await this.sendTokenOnLogin(token);
        
        if (result.success) {
          console.log('FCM Service: Successfully set up FCM on login');
          // Start periodic refresh
          this.startPeriodicRefresh();
        } else {
          console.error('FCM Service: Failed to register token on backend:', result.error);
          if (result.shouldReLogin) {
            console.warn('FCM Service: Backend indicates re-login required');
          }
        }
      } else {
        console.warn('FCM Service: Could not obtain FCM token');
      }
    } catch (error) {
      console.error('FCM Service: Error setting up FCM on login:', error);
    }
  }

  /**
   * Cleanup FCM on logout
   */
  cleanup(): void {
    console.log('FCM Service: Cleaning up FCM service...');
    this.stopPeriodicRefresh();
    this.currentToken = null;
    this.isInitialized = false;
    this.axiosInstance = null;
  }
}

// Export singleton instance
const fcmTokenService = new FCMTokenService();
export default fcmTokenService;

// Export a hook to set up the FCM service with axios instance
export const useFCMService = () => {
  const axiosInstancePromise = useAxiosInstance();
  
  // Set the axios instance in the service when it's available
  React.useEffect(() => {
    const setAxiosInstance = async () => {
      try {
        const axiosInstance = await axiosInstancePromise;
        fcmTokenService.setAxiosInstance(axiosInstance);
      } catch (error) {
        console.error('Error setting axios instance:', error);
      }
    };
    
    setAxiosInstance();
  }, [axiosInstancePromise]);
  
  return fcmTokenService;
};